import { createAsyncThunk } from "@reduxjs/toolkit";
import { postLoginService } from "../../Services";
import { alert } from "../../utils/commonFunctions/alert";

// Send collaboration proposal
export const sendCollaborationProposal = createAsyncThunk(
  "collaboration/sendProposal",
  async (proposalData, { rejectWithValue }) => {
    try {
      const response = await postLoginService.post(
        "/api/collaborations/propose",
        proposalData
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to send collaboration proposal"
      );
    }
  }
);

// Get all proposals for influencer
export const getInfluencerProposals = createAsyncThunk(
  "collaboration/getInfluencerProposals",
  async (status = "") => {
    try {
      const url = status
        ? `/api/collaborations/influencer/proposals?status=${status}`
        : `/api/collaborations/influencer/proposals`;
      const response = await postLoginService.get(url);
      if (response?.success) {
        return response;
      } else {
        throw { message: response?.message || "Failed to fetch proposals" };
      }
    } catch (error) {
      const errorMessage = error?.message || "Failed to fetch proposals";
      alert.error(errorMessage);
      throw error;
    }
  }
);

// Accept a proposal
export const acceptProposal = createAsyncThunk(
  "collaboration/acceptProposal",
  async (proposalId) => {
    try {
      const url = `/api/collaborations/influencer/proposals/${proposalId}/respond`;
      const response = await postLoginService.put(url, { accept: true });
      if (response?.success) {
        // Return the collaboration data which should contain promo code and referral link
        return {
          ...response,
          proposalId,
          collaboration: response.data || response.collaboration || null,
        };
      } else {
        alert.error(response?.message || "Failed to accept proposal");
        throw { message: response?.message || "Failed to accept proposal" };
      }
    } catch (error) {
      const errorMessage = error?.message || "Failed to accept proposal";
      alert.error(errorMessage);
      throw error;
    }
  }
);

// Reject a proposal
export const rejectProposal = createAsyncThunk(
  "collaboration/rejectProposal",
  async ({ proposalId, rejectionReason }) => {
    try {
      const url = `/api/collaborations/influencer/proposals/${proposalId}/respond`;
      const response = await postLoginService.put(url, {
        accept: false,
        rejectionReason,
      });
      if (response?.success) {
        // alert.success("Proposal rejected");
        return { ...response, proposalId };
      } else {
        alert.error(response?.message || "Failed to reject proposal");
        throw { message: response?.message || "Failed to reject proposal" };
      }
    } catch (error) {
      const errorMessage = error?.message || "Failed to reject proposal";
      alert.error(errorMessage);
      throw error;
    }
  }
);

// Get active collaborations
export const getActiveCollaborations = createAsyncThunk(
  "collaboration/getActiveCollaborations",
  async () => {
    try {
      const url = `/api/collaborations/influencer/active`;
      const response = await postLoginService.get(url);
      if (response?.success) {
        return response;
      } else {
        throw {
          message: response?.message || "Failed to fetch active collaborations",
        };
      }
    } catch (error) {
      const errorMessage =
        error?.message || "Failed to fetch active collaborations";
      alert.error(errorMessage);
      throw error;
    }
  }
);

// Get merchant proposals (proposals sent by merchant)
export const getMerchantProposals = createAsyncThunk(
  "collaboration/getMerchantProposals",
  async () => {
    try {
      const url = `/api/collaborations/merchant/proposals`;
      const response = await postLoginService.get(url);
      if (response?.success) {
        return response;
      } else {
        throw {
          message: response?.message || "Failed to fetch proposals",
        };
      }
    } catch (error) {
      const errorMessage = error?.message || "Failed to fetch proposals";
      alert.error(errorMessage);
      throw error;
    }
  }
);

// Join open collaboration
export const joinOpenCollaboration = createAsyncThunk(
  "collaboration/joinOpenCollaboration",
  async (dealId, { rejectWithValue }) => {
    try {
      const url = `/api/collaborations/join/${dealId}`;
      const response = await postLoginService.post(url);

      if (response?.success) {
        // alert.success(response.message || "Successfully joined collaboration!");
        return {
          ...response,
          dealId,
        };
      } else {
        alert.error(response?.message || "Failed to join collaboration");
        return rejectWithValue(response?.message || "Failed to join collaboration");
      }
    } catch (error) {
      const errorMessage = error?.message || "Failed to join collaboration";
      alert.error(errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

// Send influencer request to merchant (for normal deals)
export const sendInfluencerRequest = createAsyncThunk(
  "collaboration/sendInfluencerRequest",
  async (requestData, { rejectWithValue }) => {
    try {
      const url = `/api/collaborations/influencer/request`;
      const response = await postLoginService.post(url, requestData);

      if (response?.success) {
        // alert.success(response.message || "Collaboration request sent successfully!");
        return response;
      } else {
        alert.error(response?.message || "Failed to send collaboration request");
        return rejectWithValue(response?.message || "Failed to send collaboration request");
      }
    } catch (error) {
      const errorMessage = error?.message || "Failed to send collaboration request";
      alert.error(errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

// Get merchant incoming requests (requests sent by influencers to merchant)
export const getMerchantIncomingRequests = createAsyncThunk(
  "collaboration/getMerchantIncomingRequests",
  async () => {
    try {
      const url = `/api/collaborations/merchant/incoming-requests`;
      const response = await postLoginService.get(url);
      if (response?.success) {
        return response;
      } else {
        throw {
          message: response?.message || "Failed to fetch incoming requests",
        };
      }
    } catch (error) {
      const errorMessage = error?.message || "Failed to fetch incoming requests";
      alert.error(errorMessage);
      throw error;
    }
  }
);

// Merchant respond to influencer request (accept or reject)
export const respondToInfluencerRequest = createAsyncThunk(
  "collaboration/respondToInfluencerRequest",
  async ({ requestId, accept, rejectionReason = null }) => {
    try {
      const url = `/api/collaborations/merchant/requests/${requestId}/respond`;
      const payload = accept
        ? { accept: true }
        : { accept: false, rejectionReason };

      const response = await postLoginService.put(url, payload);

      if (response?.success) {
        // alert.success(accept ? "Request accepted successfully!" : "Request rejected");
        return { ...response, requestId, accept };
      } else {
        alert.error(response?.message || "Failed to respond to request");
        throw { message: response?.message || "Failed to respond to request" };
      }
    } catch (error) {
      const errorMessage = error?.message || "Failed to respond to request";
      alert.error(errorMessage);
      throw error;
    }
  }
);

// Get influencer sent requests (requests sent by influencer to merchants)
export const getInfluencerSentRequests = createAsyncThunk(
  "collaboration/getInfluencerSentRequests",
  async () => {
    try {
      const url = `/api/collaborations/influencer/sent-requests`;
      const response = await postLoginService.get(url);
      if (response?.success) {
        return response;
      } else {
        throw {
          message: response?.message || "Failed to fetch sent requests",
        };
      }
    } catch (error) {
      const errorMessage = error?.message || "Failed to fetch sent requests";
      alert.error(errorMessage);
      throw error;
    }
  }
);
